<template>
  <div class="form-wrapper p-fluid">
    <h4>Add your campaign info:</h4>
    <div class="campaign-create-text">Tell us a little about what advertising you want to get. The more details, the better.</div>
    <div class="campaign-create-sub-title">Basic info:</div>

    <FormField class="p-mt-4" v-model="campaign.title" title="Campaign name" placeholder="Add campaign name" :required="true" :error="getError('title')" autofocus/>

    <template v-if="canAddMoreProducts">
      <FormField :required="true" title="Add product" componentType="select" class="field-select-product p-mb-0" :options="filteredProducts" :error="getError('products')" optionLabel="title" placeholder="Choose product" @change="onProductSelectChange" />
      <div class="p-col p-p-0 add-new-product-button-wrapper">
        <Button label="Add new product" class="p-ml-auto p-button-text width-auto" icon="pi pi-plus" @click.prevent="showAddProductModal" />
      </div>
    </template>

    <div class="product-list-wrapper" v-if="campaign.products.length">
      <div class="product-list-title">Product</div>
      <Button label="Add one more" class="p-ml-auto width-auto" @click.prevent="showProductSelection" icon="pi pi-plus" />

      <ProductListCard
        v-for="product in campaign.products"
        :key="product.id"
        :title="product.title"
        :description="product.description"
        :price="product.value"
        :images="product.photos"
        :canEdit="false"
        @delete="onProductRemove(product)"
      />
    </div>

    <div class="campaign-create-sub-title">Ad settings:</div>

    <FormField title="Choose video format:" componentType="custom" :error="getError('ad_format_options')">
      <VideoFormats :formats="formats" :selected-formats="campaign.ad_format_options" @toggle="onToggleVideoFormat" />
    </FormField>

    <FormField title="How many creators you want to work with?" componentType="select" v-model="campaign.number_of_creators" :options="creatorsLimits" :error="getError('number_of_creators')" placeholder="Choose the number of creators" class="p-mb-0" />
    <div class="creators-limits-note">We recommend a minimum of 3 creators per campaign. This way you are able to create the best performing UGC mashup video ads. The cost is ${{ props.price }} per creator.</div>

    <div class="campaign-create-sub-title">Demographics:</div>
    <div class="campaign-create-text">You can choose several options, if necessary.</div>

    <FormField title="Creator age range" componentType="custom" class="p-mt-3" :error="getError('age')">
      <div class="p-field-checkbox" v-for="age in ages" :key="age.id">
        <Checkbox :id="'age-' + age.title" :value="age" v-model="campaign.age" />
        <label :for="'age-' + age.title">{{ age.title }}</label>
      </div>
    </FormField>

    <FormField title="Creator pronouns" componentType="custom" class="p-mt-3" :error="getError('gender')">
      <div class="p-field-checkbox" v-for="gender in genders" :key="gender.id">
        <Checkbox :id="'gender-' + gender.title" :value="gender" v-model="campaign.gender" />
        <label :for="'gender-' + gender.title">{{ gender.title }}</label>
      </div>
    </FormField>

    <FormField v-model="campaign.note"
               title="Campaign summary"
               :placeholder="'For example: This campaign is for ' + brand.name + '\'s phone cases, and will be run as paid ads on FB and IG. Creators will need to use an additional phone to model the cases.'"
               note="This is what creators will see when applying to your campaign. Describe the product, company, and usage for the video, and any supplementary materials creators will need."
               :error="getError('note')"
               componentType="textarea"
               rows="5" class="p-mt-5"
               :showNoteOnTop="true" />

    <Requirements v-model:campaign='campaign'/>

    <Questions v-model:campaign='campaign'/>

    <ProductModal v-model:visible="productModalVisible" @submit="onProductAdd" />
  </div>

  <Footer>
    <Button label="Next Step" class="p-ml-auto next-button p-button-md p-button-footer" :loading="busy" @click.prevent="submit" :disabled="!canSendForm" />
  </Footer>
</template>

<script setup>
import VideoFormats from "./VideoFormats";
import FormField from '../../FormField';
import {computed, ref, reactive, toRef, onMounted} from "vue";
import {useProduct} from "../../../hooks/useProduct";
import {useForm} from "../../../hooks/useForm";
import Footer from "./Footer";
import ProductModal from "../../product/ProductModal";
import ProductListCard from "../../ProductListCard";
import {useAdFormatOption} from "../../../hooks/useAdFormatOption";
import {useDemographic} from "../../../hooks/useDemographic";
import {useCampaign} from "../../../hooks/useCampaign";
import Requirements from './Requirements.vue';
import Questions from './Questions.vue';

const {getProducts} = useProduct();
const {getDemographics, isAge, isGender} = useDemographic();
const {getFormatOptions} = useAdFormatOption();
const {validateInfo, prepareToSave} = useCampaign();

const emit = defineEmits(['onNext']);
const props = defineProps({
  campaign: {type: Object, required: true},
  price: {type: Number, required: true},
  brand: {type: Object, required: true},
});

onMounted(() => {
  console.log('mounted');
})

const loading = ref(true);

const demographics = (await getDemographics()).data;
const formats = (await getFormatOptions()).data;
const products = (await getProducts()).data;

const creatorsLimits = ref([1, 2, 3, 4, 5, 6, 7 ,8 ,9, 10]);
const productModalVisible = ref(false);
const campaign = toRef(props, 'campaign');
const canAddMoreProducts = ref(campaign.value.products.length === 0);
const {busy, submit, getError, reset} = useForm(async () => {
  await validateInfo(prepareToSave(props.campaign));
  onNextStep();
});

loading.value = false;

const filteredProducts = computed(() => products.filter(product => !campaign.value.products.find(campaignProduct => campaignProduct.id === product.id)));
const ages = computed(() => demographics.filter(({category}) => isAge(category)))
const genders = computed(() => demographics.filter(({category}) => isGender(category)))
const canSendForm = computed(() => {
  return campaign.value.title
    && campaign.value.ad_format_options?.length
    && campaign.value.number_of_creators
    && campaign.value.products?.length
    && campaign.value.age?.length
    && campaign.value.gender?.length
    && campaign.value.note
    && campaign.value.requirements.filter(requirement => requirement.requirement === '' ).length === 0
    && campaign.value.questions.filter(question => question.question === '' ).length === 0;
})

const showAddProductModal = () => productModalVisible.value = true;
const onNextStep = () => emit('onNext');
const onProductAdd = (product) => {
  campaign.value.products.push(product);
  productModalVisible.value = false;
  canAddMoreProducts.value = false
}
const onProductRemove = ({ id }) => {
  const index = campaign.value.products?.findIndex(product => product.id === id);

  if (index > -1) {
    campaign.value.products.splice(index, 1);
  }

  if (!campaign.value.products.length) {
    canAddMoreProducts.value = true;
  }
}

const showProductSelection = () => canAddMoreProducts.value = true;
const onProductSelectChange = ({ value: product}) => {
  campaign.value.products.push(product);
  canAddMoreProducts.value = false;
}

const onToggleVideoFormat = ({ id }) => {
  campaign.value.ad_format_options = [];
  campaign.value.ad_format_options.push(id);
};

</script>

<style lang="scss" scoped>
@import "../../../assets/sass/_theme_variables.scss";

.form-wrapper {
  width: toRem(548);
  margin: 0 auto;
}

.campaign-create-text {
  margin-bottom: toRem(20);
  font-size: 18px;
  color: $textSecondaryColor;
}

.campaign-create-sub-title {
  margin: toRem(30) 0 toRem(12);
  font-size: 18px;
  font-weight: bold;
}

.p-field {
  margin-bottom: toRem(22);
}

.product-list-wrapper {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: toRem(40);
  align-items: center;
}

.product-list-title {
  font-weight: 600;
}

.add-new-product-button-wrapper + .product-list-wrapper {
  margin-top: 1rem;
}

.creators-limits-note {
  margin-top: toRem(10);
  margin-bottom: toRem(35);
}
</style>
